/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "..";

type PasswordRuleKey =
  | "characters"
  | "lowercase"
  | "uppercase"
  | "number"
  | "special";

type PasswordRule = {
  key: PasswordRuleKey;
  test: (value: string) => boolean;
};

// Explicit ASCII special set — a negated class like /[^A-Za-z0-9]/ would count
// Arabic or accented letters as "special", contradicting the hint text.
const SPECIAL_CHAR_REGEX = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;

export const PASSWORD_RULES: PasswordRule[] = [
  { key: "characters", test: (value) => value.length >= 12 },
  { key: "lowercase", test: (value) => /[a-z]/.test(value) },
  { key: "uppercase", test: (value) => /[A-Z]/.test(value) },
  { key: "number", test: (value) => /[0-9]/.test(value) },
  { key: "special", test: (value) => SPECIAL_CHAR_REGEX.test(value) },
];

export const isPasswordRulesMet = (value: string) =>
  PASSWORD_RULES.every((rule) => rule.test(value));

type Props = {
  password: string;
  className?: string;
};

const PasswordRulesChecklist = ({ password, className }: Props) => {
  const { t } = useTranslation();

  return (
    <ul className={classNames("flex flex-col gap-y-s5", className)}>
      {PASSWORD_RULES.map(({ key, test }) => {
        const isMet = test(password);
        return (
          <li key={key} className="flex items-center gap-x-2">
            <FontAwesomeIcon
              name={isMet ? "fa-check" : "fa-xmark"}
              className={classNames(
                "p-1",
                isMet
                  ? "-text--theme-colors-success-default"
                  : "-text--theme-colors-danger-default",
              )}
              size="sm"
            />
            <span
              className={classNames(
                "text-xs",
                isMet
                  ? "-text--theme-colors-success-default"
                  : "-text--theme-colors-text-body",
              )}
            >
              {t(`auth.register.passwordInstruction.${key}`)}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default PasswordRulesChecklist;
